import {useState, useEffect, useRef, RefObject} from "react";

export interface UseInvestHeaderMenuReturn {
    availableWidth: number;
    filterShow: boolean;
    setFilterShow: (show: boolean) => void;
    rowRef: RefObject<HTMLDivElement>;
    titleRef: RefObject<HTMLHeadingElement>;
    filterRef: RefObject<HTMLDivElement>;
    menuContainerRef: RefObject<HTMLDivElement>;
}

/**
 * Хук для расчета доступной ширины и управления refs для меню заголовка инвестиций
 * @returns {UseInvestHeaderMenuReturn} Объект с availableWidth, filterShow, setFilterShow и refs
 */
export default function useInvestHeaderMenu(): UseInvestHeaderMenuReturn {
    const rowRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const filterRef = useRef<HTMLDivElement>(null);
    const menuContainerRef = useRef<HTMLDivElement>(null);
    const [availableWidth, setAvailableWidth] = useState<number>(0);
    const [filterShow, setFilterShow] = useState<boolean>(false);

    // Вычисляем availableWidth для OverflowActionsMenu
    useEffect(() => {
        let animationFrameId: number | null = null;

        const calculateAvailableWidth = () => {
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }

            animationFrameId = requestAnimationFrame(() => {
                if (!rowRef.current || !titleRef.current || !menuContainerRef.current) {
                    animationFrameId = null;
                    return;
                }

                const menuContainerRect = menuContainerRef.current.getBoundingClientRect();
                const titleRect = titleRef.current.getBoundingClientRect();

                const menuContainerStyle = window.getComputedStyle(menuContainerRef.current);
                const menuContainerPaddingLeft = parseFloat(menuContainerStyle.paddingLeft) || 0;
                const menuContainerPaddingRight = parseFloat(menuContainerStyle.paddingRight) || 0;

                const titleStyle = window.getComputedStyle(titleRef.current);
                const titleMarginRight = parseFloat(titleStyle.marginRight) || 0;

                const menuContainerWidth = menuContainerRect.width - menuContainerPaddingLeft - menuContainerPaddingRight;
                const titleWidth = titleRect.width;
                const available = menuContainerWidth - titleWidth - titleMarginRight;

                setAvailableWidth(Math.max(0, available));
                animationFrameId = null;
            });
        };

        calculateAvailableWidth();

        // ResizeObserver для отслеживания изменений размеров элементов
        const resizeObserver = new ResizeObserver(() => {
            calculateAvailableWidth();
        });

        if (menuContainerRef.current) {
            resizeObserver.observe(menuContainerRef.current);
        }
        if (titleRef.current) {
            resizeObserver.observe(titleRef.current);
        }

        // Также слушаем изменения размера окна
        window.addEventListener('resize', calculateAvailableWidth);

        return () => {
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
            resizeObserver.disconnect();
            window.removeEventListener('resize', calculateAvailableWidth);
        };
    }, []);

    return {
        availableWidth,
        filterShow,
        setFilterShow,
        rowRef,
        titleRef,
        filterRef,
        menuContainerRef,
    };
}

